import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSessionValidation } from "@/hooks/useSessionValidation";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { calculateUTCBookingTimes } from "@/utils/timezoneUtils";
import { useGetOrCreatePeriodCredits, useUseCouponCredit } from "@/hooks/useUserCouponCredits";
import { AvailableCoupon } from "@/hooks/useBookingCoupons";

interface UseBookingHandlersProps {
  providerId?: string;
  serviceId?: string;
  selectedDate?: Date;
  selectedTimeSlot: string;
  selectedMembershipId: string | null;
  selectedCoupon: AvailableCoupon | null;
  availableTimeSlots?: any[];
  selectedService?: any;
  userMemberships?: any[];
  hasActiveWaivers: boolean;
  waiversCompleted: boolean;
  couponDiscount: number;
  isCreditBasedCoupon: boolean;
  setProcessing: (processing: boolean) => void;
}

export const useBookingHandlers = ({
  providerId,
  serviceId,
  selectedDate,
  selectedTimeSlot,
  selectedMembershipId,
  selectedCoupon,
  availableTimeSlots,
  selectedService,
  userMemberships,
  hasActiveWaivers,
  waiversCompleted,
  couponDiscount,
  isCreditBasedCoupon,
  setProcessing,
}: UseBookingHandlersProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { validateSession } = useSessionValidation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const getOrCreatePeriodCredits = useGetOrCreatePeriodCredits();
  const useCouponCredit = useUseCouponCredit();

  // Helper function to validate session before booking operations
  const validateBookingSession = async (): Promise<boolean> => {
    console.log('=== BOOKING SESSION VALIDATION ===');
    
    const isValid = await validateSession();
    if (!isValid) {
      console.log('Booking session validation failed');
      setProcessing(false);
      return false;
    }
    
    console.log('Booking session validation passed');
    return true;
  };

  // Helper function to add provider to favorites
  const addProviderToFavorites = async (providerId: string, userId: string) => {
    try {
      const { data: existingFavorite, error: checkError } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('provider_id', providerId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking existing favorite:', checkError);
        return false;
      }

      if (existingFavorite) {
        console.log('Provider already in favorites, skipping add');
        return true;
      }

      const { error: insertError } = await supabase
        .from('user_favorites')
        .insert({
          user_id: userId,
          provider_id: providerId
        });

      if (insertError) {
        console.error('Error adding provider to favorites:', insertError);
        return false;
      }

      console.log('Successfully added provider to user favorites');
      return true;
    } catch (error) {
      console.error('Unexpected error adding to favorites:', error);
      return false;
    }
  };

  const handleBookingWithMembership = async () => {
    if (!selectedDate || !selectedTimeSlot || !user || !selectedMembershipId) {
      toast({
        title: "Missing Information",
        description: "Please select a date, time slot, and membership",
        variant: "destructive",
      });
      return;
    }

    if (hasActiveWaivers && !waiversCompleted) {
      toast({
        title: "Waivers Required",
        description: "Please complete all required waivers before booking",
        variant: "destructive",
      });
      return;
    }
    
    setProcessing(true);
    
    // Validate session before proceeding
    const sessionValid = await validateBookingSession();
    if (!sessionValid) return;
    
    try {
      console.log("Creating booking with membership...");
      
      const selectedTimeSlotData = availableTimeSlots?.find(slot => slot.id === selectedTimeSlot);
      
      if (!selectedTimeSlotData) {
        throw new Error("Selected time slot not found");
      }

      const customerName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || user.email?.split('@')[0] || 'Customer';

      const { startTime: bookingStartTimeUTC, endTime: bookingEndTimeUTC } = calculateUTCBookingTimes(
        selectedTimeSlotData.start_time,
        selectedService?.duration_minutes || 60
      );
      
      const localBookingDate = format(selectedDate, 'yyyy-MM-dd');

      const { data: bookingResult, error: bookingError } = await supabase.rpc(
        'create_booking_with_slot_update',
        {
          p_user_id: user.id,
          p_provider_id: providerId,
          p_service_id: serviceId,
          p_time_slot_id: selectedTimeSlot,
          p_customer_name: customerName,
          p_customer_email: user.email,
          p_booking_date: localBookingDate,
          p_start_time: bookingStartTimeUTC,
          p_end_time: bookingEndTimeUTC,
          p_duration_minutes: selectedService?.duration_minutes || 60,
          p_price: 0,
          p_currency: 'GBP',
          p_stripe_session_id: null,
          p_expected_booking_count: selectedTimeSlotData.current_bookings
        }
      );

      if (bookingError) {
        console.error('Atomic booking creation error:', bookingError);
        throw new Error(`Failed to create booking: ${bookingError.message}`);
      }

      const bookingId = bookingResult[0]?.booking_id;
      
      if (!bookingId) {
        throw new Error('Booking was created but no booking ID was returned');
      }

      // Add membership_id to the booking
      const { error: membershipUpdateError } = await supabase
        .from('bookings')
        .update({ 
          membership_id: selectedMembershipId,
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (membershipUpdateError) {
        console.error('Failed to add membership_id to booking:', membershipUpdateError);
        toast({
          title: "Booking Created",
          description: "Booking was created successfully, but there was an issue linking the membership. Please contact support if needed.",
          variant: "default",
        });
      }

      // Update user membership
      const selectedMembership = userMemberships?.find(m => m.id === selectedMembershipId);
      if (selectedMembership) {
        const membershipType = selectedMembership.membership?.membership_type;
        
        if (membershipType === 'unlimited') {
          // For unlimited, just increment sessions_used (no sessions_remaining to decrement)
          const newSessionsUsed = (selectedMembership.sessions_used || 0) + 1;
          const { error: membershipUpdateError } = await supabase
            .from('user_memberships')
            .update({ 
              sessions_used: newSessionsUsed,
              updated_at: new Date().toISOString()
            })
            .eq('id', selectedMembershipId);

          if (membershipUpdateError) {
            console.error('Membership update error:', membershipUpdateError);
          }
        } else if (membershipType === 'pack_of_sessions' || membershipType === 'sessions_per_month') {
          const newSessionsRemaining = Math.max(0, (selectedMembership.sessions_remaining || 0) - 1);
          const newSessionsUsed = (selectedMembership.sessions_used || 0) + 1;
          
          const { error: membershipUpdateError } = await supabase
            .from('user_memberships')
            .update({ 
              sessions_remaining: newSessionsRemaining,
              sessions_used: newSessionsUsed,
              updated_at: new Date().toISOString()
            })
            .eq('id', selectedMembershipId);

          if (membershipUpdateError) {
            console.error('Membership update error:', membershipUpdateError);
          }
        }
      }

      // Auto-add provider to user's favorites
      await addProviderToFavorites(providerId || '', user.id);

      // Cache invalidation
      queryClient.removeQueries({ queryKey: ['available-time-slots'] });
      queryClient.removeQueries({ queryKey: ['time-slots'] });
      queryClient.removeQueries({ queryKey: ['user-memberships'] });
      queryClient.removeQueries({ queryKey: ['favorites'] });

      await new Promise(resolve => setTimeout(resolve, 500));
      
      await queryClient.refetchQueries({ 
        queryKey: ['available-time-slots', serviceId, selectedDate.toISOString()],
        type: 'active'
      });

      // toast({
      //   title: "Booking Confirmed!",
      //   description: "Your booking has been confirmed using your membership.",
      // });
      
      navigate(`/booking-success?booking_id=${bookingId}&membership=true`);
    } catch (error) {
      console.error('Membership booking error:', error);
      toast({
        title: "Error",
        description: "Failed to create booking with membership. Please try again.",
        variant: "destructive",
      });
      setProcessing(false);
    }
  };

  const handleFreeBooking = async () => {
    if (!selectedDate || !selectedTimeSlot || !user) {
      toast({
        title: "Missing Information",
        description: "Please select a date and time slot",
        variant: "destructive",
      });
      return;
    }

    if (hasActiveWaivers && !waiversCompleted) {
      toast({
        title: "Waivers Required",
        description: "Please complete all required waivers before booking",
        variant: "destructive",
      });
      return;
    }
    
    setProcessing(true);

    // Validate session before proceeding
    const sessionValid = await validateBookingSession();
    if (!sessionValid) return;
    
    try {
      const selectedTimeSlotData = availableTimeSlots?.find(slot => slot.id === selectedTimeSlot);
      
      if (!selectedTimeSlotData) {
        throw new Error("Selected time slot not found");
      }

      const customerName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || user.email?.split('@')[0] || 'Customer';

      const { startTime: bookingStartTimeUTC, endTime: bookingEndTimeUTC } = calculateUTCBookingTimes(
        selectedTimeSlotData.start_time,
        selectedService?.duration_minutes || 60
      );
      
      const localBookingDate = format(selectedDate, 'yyyy-MM-dd');

      // Handle credit-based coupons
      let userCouponCreditId: string | undefined;
      if (isCreditBasedCoupon && selectedCoupon) {
        const periodType = selectedCoupon.coupon.discount_type === 'weekly_credits' ? 'weekly' : 'monthly';
        const creditsPerPeriod = selectedCoupon.coupon.credits_per_period || 1;
        
        const periodCredits = await getOrCreatePeriodCredits.mutateAsync({
          userCouponId: selectedCoupon.id,
          creditsPerPeriod: creditsPerPeriod,
          periodType: periodType as 'weekly' | 'monthly',
          bookingDate: selectedDate
        });

        const availableCredits = periodCredits.credits_available - periodCredits.credits_used;
        
        if (availableCredits <= 0) {
          toast({
            title: "No Credits Available",
            description: `You have used all your ${periodType} credits for this period.`,
            variant: "destructive",
          });
          setProcessing(false);
          return;
        }

        userCouponCreditId = periodCredits.id;
      }

      const { data: bookingResult, error: bookingError } = await supabase.rpc(
        'create_booking_with_slot_update',
        {
          p_user_id: user.id,
          p_provider_id: providerId,
          p_service_id: serviceId,
          p_time_slot_id: selectedTimeSlot,
          p_customer_name: customerName,
          p_customer_email: user.email,
          p_booking_date: localBookingDate,
          p_start_time: bookingStartTimeUTC,
          p_end_time: bookingEndTimeUTC,
          p_duration_minutes: selectedService?.duration_minutes || 60,
          p_price: 0,
          p_currency: 'GBP',
          p_stripe_session_id: null,
          p_expected_booking_count: selectedTimeSlotData.current_bookings
        }
      );

      if (bookingError) {
        throw new Error(`Failed to create booking: ${bookingError.message}`);
      }
      
      const bookingId = bookingResult[0]?.booking_id;

      // Apply coupon to booking
      if (selectedCoupon) {
        const { error: couponError } = await supabase
          .from('booking_coupons')
          .insert({
            booking_id: bookingId,
            user_coupon_id: selectedCoupon.id,
            discount_amount: couponDiscount
          });

        if (!couponError) {
          if (isCreditBasedCoupon && userCouponCreditId) {
            try {
              await useCouponCredit.mutateAsync({
                userCouponCreditId: userCouponCreditId
              });
            } catch (creditError) {
              console.error('Error using credit:', creditError);
            }
          } else if (!isCreditBasedCoupon) {
            await supabase
              .from('user_coupons')
              .update({
                times_used: (selectedCoupon.times_used || 0) + 1
              })
              .eq('id', selectedCoupon.id);
          }
        }
      }

      // Auto-add provider to user's favorites
      await addProviderToFavorites(providerId || '', user.id);

      // Cache invalidation
      queryClient.removeQueries({ queryKey: ['available-time-slots'] });
      queryClient.removeQueries({ queryKey: ['time-slots'] });
      queryClient.removeQueries({ queryKey: ['user-coupon-credits'] });
      queryClient.removeQueries({ queryKey: ['favorites'] });

      await new Promise(resolve => setTimeout(resolve, 500));
      
      await queryClient.refetchQueries({ 
        queryKey: ['available-time-slots', serviceId, selectedDate.toISOString()],
        type: 'active'
      });

      // toast({
      //   title: "Booking Confirmed!",
      //   description: "Your free booking has been confirmed using your coupon.",
      // });
      
      navigate(`/booking-success?booking_id=${bookingId}&coupon=true`);
    } catch (error) {
      console.error('Free booking error:', error);
      toast({
        title: "Error",
        description: "Failed to create free booking. Please try again.",
        variant: "destructive",
      });
      setProcessing(false);
    }
  };

  const handleRegularBooking = async () => {
    if (!selectedDate || !selectedTimeSlot || !user) {
      toast({
        title: "Missing Information",
        description: "Please select a date and time slot",
        variant: "destructive",
      });
      return;
    }

    if (hasActiveWaivers && !waiversCompleted) {
      toast({
        title: "Waivers Required",
        description: "Please complete all required waivers before booking",
        variant: "destructive",
      });
      return;
    }
    
    setProcessing(true);

    // Validate session before proceeding
    const sessionValid = await validateBookingSession();
    if (!sessionValid) return;
    
    try {
      const localBookingDate = format(selectedDate, 'yyyy-MM-dd');
      
      const { data, error } = await supabase.functions.invoke('create-booking-payment', {
        body: {
          serviceId,
          timeSlotId: selectedTimeSlot,
          selectedDate: localBookingDate,
          couponDiscount: couponDiscount,
          selectedCouponId: selectedCoupon?.id
        }
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received from payment session");
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      toast({
        title: "Error",
        description: "Failed to create payment session. Please try again.",
        variant: "destructive",
      });
      setProcessing(false);
    }
  };

  return {
    handleBookingWithMembership,
    handleFreeBooking,
    handleRegularBooking,
  };
};
